import {Exclude, Expose, instanceToPlain, plainToInstance, Type} from "class-transformer";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {GeoCoord} from "./geo-coord";
import {TransportEntity} from "./transport";
import {Entity} from "./entity";
import {GeofenceEvent, GeofenceEventValidator} from "./geofence-event";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Message, MessageArrayValidator, MessageEntity} from "./message";

/** Validator for Shift JSON */
export const ShiftValidator = t.intersection([
  t.type({
    id: t.string,
    personId: t.string,
    regionId: t.string,
    startDttm: t.number
  }),
  t.partial({
    trailerId: t.string,
    vehicleId: t.string,
    vehicleName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    endDttm: t.number,
    preDttm: t.number,
    postDttm: t.number,
    startLatCoord: t.number,
    startLongCoord: t.number,
    endLatCoord: t.number,
    endLongCoord: t.number,
    startOdometer: t.number,
    endOdometer: t.number,
    odometerUom: t.string,
    shiftTypeId: t.string,
    shiftTypeName: t.string,
    /** Minutes billed before the shift started and after it ended. */
    preBillableDttm: t.number,
    postBillableDttm: t.number,
    billableStartDttm: t.number,
    billableEndDttm: t.number,
    /** When the driver's tablet ended the shift, which an edited end time does not change. */
    altEndDttm: t.number,
    driverVerified: t.boolean,
    driverDenied: t.boolean,
    holdingRecord: t.boolean,
    tabletHolding: t.boolean,
    /** Server-built summaries shown on the driver activity shift rows. */
    shiftTrailerDriverText: t.string,
    shiftTotalDurationText: t.string,
    startGeofenceEvent: GeofenceEventValidator,
    endGeofenceEvent: GeofenceEventValidator,
    revenue: t.number,
    cost: t.number,
    nextStopSiteId: t.string,
    nextStopNote: t.string,
    nextStopDeclaredDttm: t.number,
    startLocationAccuracy: t.string,
    startLocationPermission: t.string,
    endLocationAccuracy: t.string,
    endLocationPermission: t.string,
    personName: t.string,
    personCompanyId: t.string,
    personCompanyName: t.string,
    trailerName: t.string,
    trailerCompanyId: t.string,
    trailerCompanyName: t.string,
    trailerParentCompanyId: t.string,
    trailerParentCompanyName: t.string,
    messages: MessageArrayValidator,
    database: t.string,
    loadedMileage: t.number,
    unloadedMileage: t.number,
    odometerLoadedMileage: t.number,
    odometerUnloadedMileage: t.number,
    shiftOdometerLoadedMileage: t.number,
    shiftOdometerUnloadedMileage: t.number,
    gpsLoadedMileage: t.number,
    gpsUnloadedMileage: t.number,
    shiftGpsLoadedMileage: t.number,
    shiftGpsUnloadedMileage: t.number,
    projectedLoadedMileage: t.number,
    projectedUnloadedMileage: t.number,
    projectedOdometerLoadedMileage: t.number,
    projectedOdometerUnloadedMileage: t.number,
    projectedShiftOdometerLoadedMileage: t.number,
    projectedShiftOdometerUnloadedMileage: t.number,
    projectedGpsLoadedMileage: t.number,
    projectedGpsUnloadedMileage: t.number,
    projectedShiftGpsLoadedMileage: t.number,
    projectedShiftGpsUnloadedMileage: t.number,
  })
]);

/** Validator for an JSON array of Shifts */
export const ShiftArrayValidator = t.array(ShiftValidator);

export type Shift = t.TypeOf<typeof ShiftValidator>;
export type ShiftArray = t.TypeOf<typeof ShiftArrayValidator>;

/**
 * A shift.
 */
@Exclude()
export class ShiftEntity implements Entity {
  @Expose() id!: string;
  @Expose() regionId!: string;
  @Expose() personId!: string;
  @Expose() trailerId?: string;
  @Expose() vehicleId?: string;
  @Expose() vehicleName?: string;
  @Expose() startLatCoord?: number;
  @Expose() startLongCoord?: number;
  @Expose() endLatCoord?: number;
  @Expose() endLongCoord?: number;
  @Expose() startDttm!: number;
  @Expose() endDttm?: number;
  @Expose() preDttm?: number
  @Expose() postDttm?: number
  @Expose() startOdometer?: number
  @Expose() endOdometer?: number
  @Expose() odometerUom?: string
  @Expose() shiftTypeId?: string
  @Expose() readonly shiftTypeName?: string
  @Expose() preBillableDttm?: number
  @Expose() postBillableDttm?: number
  @Expose() billableStartDttm?: number
  @Expose() billableEndDttm?: number
  @Expose() altEndDttm?: number
  @Expose() driverVerified?: boolean
  @Expose() driverDenied?: boolean
  @Expose() readonly holdingRecord?: boolean
  @Expose() readonly tabletHolding?: boolean
  @Expose() readonly shiftTrailerDriverText?: string
  @Expose() readonly shiftTotalDurationText?: string
  @Expose() readonly startGeofenceEvent?: GeofenceEvent
  @Expose() readonly endGeofenceEvent?: GeofenceEvent
  @Expose() readonly revenue?: number
  @Expose() readonly cost?: number
  @Expose() nextStopSiteId?: string
  @Expose() nextStopNote?: string
  @Expose() nextStopDeclaredDttm?: number
  @Expose() startLocationAccuracy?: string
  @Expose() startLocationPermission?: string
  @Expose() endLocationAccuracy?: string
  @Expose() endLocationPermission?: string
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;
  @Expose() readonly personName?: string;
  @Expose() readonly personCompanyId?: string;
  @Expose() readonly personCompanyName?: string;
  @Expose() readonly trailerName?: string;
  @Expose() readonly trailerCompanyId?: string;
  @Expose() readonly trailerCompanyName?: string;
  @Expose() readonly trailerParentCompanyId?: string;
  @Expose() readonly trailerParentCompanyName?: string;
  @Expose() readonly database?: string;

  @Expose()
  loadedMileage?: number;

  @Expose()
  unloadedMileage?: number;

  @Expose()
  odometerLoadedMileage?: number;

  @Expose()
  odometerUnloadedMileage?: number;

  @Expose()
  shiftOdometerLoadedMileage?: number;

  @Expose()
  shiftOdometerUnloadedMileage?: number;

  @Expose()
  gpsLoadedMileage?: number;

  @Expose()
  gpsUnloadedMileage?: number;

  @Expose()
  shiftGpsLoadedMileage?: number;

  @Expose()
  shiftGpsUnloadedMileage?: number;

  @Expose()
  projectedLoadedMileage?: number;

  @Expose()
  projectedUnloadedMileage?: number;

  @Expose()
  projectedOdometerLoadedMileage?: number;

  @Expose()
  projectedOdometerUnloadedMileage?: number;

  @Expose()
  projectedShiftOdometerLoadedMileage?: number;

  @Expose()
  projectedShiftOdometerUnloadedMileage?: number;

  @Expose()
  projectedGpsLoadedMileage?: number;

  @Expose()
  projectedGpsUnloadedMileage?: number;

  @Expose()
  projectedShiftGpsLoadedMileage?: number;

  @Expose()
  projectedShiftGpsUnloadedMileage?: number;

  @Expose()
  @Type(() => MessageEntity)
  messages: MessageEntity[] = [];

  transports: TransportEntity[] = [];

  readonly entityType = "SHIFT";

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for ShiftEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a ShiftEntity.
   *
   * @param personId The primary key of the person.
   * @param regionId The primary key of the region.
   * @param startDate The start date.
   * @param id (optional) The primary key of the shift.
   */
  static create(
    personId: string,
    regionId: string,
    startDttm: number,
    id: string | undefined = uuid()
  ): ShiftEntity {
    const entity = new ShiftEntity();
    entity.id = id;
    entity.personId = personId;
    entity.regionId = regionId;
    entity.startDttm = startDttm;
    return entity;
  }

  static from(value: Shift): ShiftEntity {
    return plainToInstance(ShiftEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<Shift> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ShiftValidator, object);
  }

  asTypeValue(): Shift {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ShiftValidator, object);
  }

  get startCoordinates(): GeoCoord | undefined {
    if (this.startLatCoord && this.startLongCoord) {
      return {
        latCoord: this.startLatCoord,
        longCoord: this.startLongCoord
      };
    } else {
      return undefined;
    }
  }

  set startCoordinates(coordinates: GeoCoord | undefined) {
    if (coordinates) {
      this.startLatCoord = coordinates.latCoord;
      this.startLongCoord = coordinates.longCoord;
    } else {
      this.startLatCoord = undefined;
      this.startLongCoord = undefined;
    }
  }

  get endCoordinates(): GeoCoord | undefined {
    if (this.endLatCoord && this.endLongCoord) {
      return {
        latCoord: this.endLatCoord,
        longCoord: this.endLongCoord
      };
    } else {
      return undefined;
    }
  }

  set endCoordinates(coordinates: GeoCoord | undefined) {
    if (coordinates) {
      this.endLatCoord = coordinates.latCoord;
      this.endLongCoord = coordinates.longCoord;
    } else {
      this.endLatCoord = undefined;
      this.endLongCoord = undefined;
    }
  }
}

export interface ShiftSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  companyIds?: string[];
  regionIds?: string[];
  vehicleIds?: string[];
  trailerIds?: string[];
  personIds?: string[];
}

export type ShiftPayload = Partial<Omit<Shift, 'messages'>> & {
  messages: Partial<Message>[]
}
